<script setup lang="ts">
import { useCVSectionTranslations } from '~/composables/useCVSectionTranslations';
import { CV_ACCENT_PRESETS, CV_FONTS, fullSectionOrder, resolveDesign, type CvSectionId } from '~/constants/cvDesign';
import { isRTLLanguage } from '~/constants/cvLanguages';
import { getLinkDisplayName, getLinkMeta } from '~/constants/linkTypes';
import type { CVData, Education as OldEducation, Experience as OldExperience } from '~/types';
import type { Education as CvEducation, Experience as CvExperience } from '~/types/cv';
import { formatDateOfBirth } from '~/utils/dateHelpers';

/**
 * Fourth business-web template, "Signature": the web CV that takes the
 * editor's Appearance panel (constants/cvDesign.ts) the way the PDF does.
 *
 * - Accent colour: the hero, headings, timeline, chips, buttons and the
 *   contact band, with text colours derived from it so a light pick stays
 *   readable (see `accentVars`).
 * - Typeface: the picked face for the whole page, from the same static cuts
 *   the PDF embeds (assets/fonts/cv-design-fonts.css).
 * - Density: type and spacing scale together, as the PDF's zoom does.
 * - Section order: the body follows it; the header and the contact band stay
 *   first and last, as the PDF keeps the header and the consent clause.
 * - Levels: skill dots and language bars only with "show levels" on.
 *
 * Page size has no meaning for a web page and is ignored.
 *
 * Layout follows the template's own width (container queries), not the
 * window's: the editor previews it in a column narrower than the screen.
 */

type TimelineExperience = {
  company?: string;
  title?: string;
  position?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
  description?: string;
}

type TimelineEducation = {
  school: string;
  degree?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
}

const props = defineProps<{
  cvData: CVData;
  experiencesList: Array<TimelineExperience | CvExperience | OldExperience>;
  educationList: Array<TimelineEducation | CvEducation | OldEducation>;
  showQRCode?: boolean;
}>();

const { t } = useI18n()
const route = useRoute()
const { publicCvLinkPath } = usePublicCvLinks()

// Section labels follow the CV's own language, not the visitor's UI language.
// `cvLanguage` is on the editor's CV type (types/cv.ts), not on the older one the templates take
const cvLanguage = computed(() => (props.cvData as { cvLanguage?: string } | undefined)?.cvLanguage || 'cs')
const { translations: sectionTranslations } = useCVSectionTranslations(cvLanguage)
const isRTL = computed(() => isRTLLanguage(cvLanguage.value))

// Levels, dates and the word for "present" read the way the PDF reads them
const { skillLevel, languageLevel, languagePercent, formatPeriod, formatCertificateDate } = useWebCv(
  () => props.cvData,
  () => ({ present: sectionTranslations.value.present || t('cv.edit.present'), nativeSpeaker: sectionTranslations.value.nativeSpeaker }),
)

const design = computed(() => resolveDesign(props.cvData?.design))

/** The template's own colour when the CV has none: the green the PDF templates ship with. */
const DEFAULT_ACCENT = CV_ACCENT_PRESETS[0]!.hex

const INK = '#0e0e0e'
/** The page background in dark mode (`--sig-bg` below). */
const DARK_BG = '#0f1115'

function luminance(hex: string): number {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)
}

function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * The accent and what it takes to read it. Any #rrggbb can be picked, so a
 * pale yellow must not end up as white text on yellow or yellow headings on
 * white: text on the accent goes ink or white, whichever reads better, and the
 * accent used as text is darkened (light page) or lightened (dark page) until
 * it clears WCAG AA for normal text.
 */
const accentVars = computed(() => {
  const accent = design.value.accentColor ?? DEFAULT_ACCENT
  const onAccent = contrast(accent, '#ffffff') >= contrast(accent, INK) ? '#ffffff' : INK

  /** The accent, mixed toward black or white in steps until it reads on `background`. */
  const readableOn = (background: string, toward: string) => {
    let share = 100
    while (share > 30 && contrast(mix(accent, toward, share), background) < 4.5) share -= 10
    return mix(accent, toward, share)
  }

  return {
    '--sig-accent': accent,
    '--sig-on-accent': onAccent,
    '--sig-accent-text-light': readableOn('#ffffff', '#000000'),
    '--sig-accent-text-dark': readableOn(DARK_BG, '#ffffff'),
  }
})

/** `share`% of `color` mixed into `toward`, as #rrggbb (an sRGB mix, like CSS color-mix). */
function mix(color: string, toward: string, share: number): string {
  const channel = (value: string, i: number) => parseInt(value.slice(i, i + 2), 16)
  return `#${[1, 3, 5]
    .map(i => Math.round((channel(color, i) * share + channel(toward, i) * (100 - share)) / 100))
    .map(c => c.toString(16).padStart(2, '0'))
    .join('')}`
}

const DEFAULT_HEADING_STACK = "'DM Sans', Inter, Arial, sans-serif"
const DEFAULT_BODY_STACK = 'Inter, Arial, sans-serif'

/** Template: DM Sans headings over Inter, as the site. A picked face sets both. */
const fontVars = computed(() => {
  const picked = design.value.font === 'default' ? null : CV_FONTS.find(font => font.id === design.value.font)
  return {
    '--sig-heading-font': picked?.previewStack ?? DEFAULT_HEADING_STACK,
    '--sig-body-font': picked?.previewStack ?? DEFAULT_BODY_STACK,
  }
})

/** Type size and the spacing step per density; both scale, as the PDF's zoom scales both. */
const DENSITY_VARS = {
  compact: { '--sig-size': '15px', '--sig-space': '0.72' },
  normal: { '--sig-size': '16px', '--sig-space': '1' },
  spacious: { '--sig-size': '17px', '--sig-space': '1.28' },
} as const

const rootStyle = computed(() => ({
  ...accentVars.value,
  ...fontVars.value,
  ...DENSITY_VARS[design.value.density],
}))

const initials = computed(() => {
  const parts = (props.cvData?.name || '').trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.charAt(0) ?? '') + (parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : '')).toUpperCase() || 'CV'
})

const contact = computed(() => props.cvData?.contact ?? undefined)

/** The card's own public URL, which is what the QR code encodes. */
const publicCardUrl = computed(() => {
  const id = route.params.id
  if (!id) return ''
  const path = publicCvLinkPath('card', String(id))
  return import.meta.client ? `${window.location.origin}${path}` : path
})

/** Location, date of birth and driving licence under the name; each is dropped when empty. */
const personalDetails = computed(() => {
  const dateOfBirth = formatDateOfBirth(contact.value?.dateOfBirth, cvLanguage.value)
  return [
    { key: 'location', icon: 'bi bi-geo-alt', label: sectionTranslations.value.location, value: contact.value?.location ?? '' },
    { key: 'dateOfBirth', icon: 'bi bi-calendar3', label: sectionTranslations.value.dateOfBirth, value: dateOfBirth },
    { key: 'drivingLicense', icon: 'bi bi-car-front', label: sectionTranslations.value.drivingLicense, value: props.cvData?.drivingLicense ?? '' },
  ].filter(item => item.value && item.value.trim())
})

/** Older CVs keep one flat `skills` list; it shows as the technical group. */
const skills = computed(() => {
  const technical = props.cvData?.technicalSkills ?? []
  const soft = props.cvData?.softSkills ?? []
  return {
    technical: technical.length || soft.length ? technical : (props.cvData?.skills ?? []),
    soft,
  }
})

const languages = computed(() => (props.cvData?.languages ?? []).filter(lang => lang?.name?.trim()))
const websites = computed(() => (props.cvData?.websites ?? []).filter(site => site?.url))
const certificates = computed(() => (props.cvData?.certificates ?? []).filter(cert => cert && (cert.name || cert.issuer)))
const hobbies = computed(() => (props.cvData?.hobbies ?? []).filter(hobby => hobby && hobby.trim()))
const customSections = computed(() =>
  (props.cvData?.customSections ?? []).filter(section => section?.sectionTitle && section.items?.length))

type Block = { key: string; id: CvSectionId; title: string; customIndex?: number }

/**
 * The body, in the CV's section order: only sections with something in them,
 * numbered as they appear. Every custom section is a block of its own, in
 * the place `custom` takes in the order.
 */
const blocks = computed<Block[]>(() => {
  const labels = sectionTranslations.value
  const result: Block[] = []
  for (const id of fullSectionOrder(design.value.sectionOrder)) {
    switch (id) {
      case 'about':
        if (props.cvData?.aboutMe?.trim()) result.push({ key: id, id, title: labels.aboutMe })
        break
      case 'experience':
        if (props.experiencesList?.length) result.push({ key: id, id, title: labels.experiences })
        break
      case 'education':
        if (props.educationList?.length) result.push({ key: id, id, title: labels.education })
        break
      case 'skills':
        if (skills.value.technical.length || skills.value.soft.length) result.push({ key: id, id, title: labels.skills })
        break
      case 'languages':
        if (languages.value.length) result.push({ key: id, id, title: labels.languages })
        break
      case 'certificates':
        if (certificates.value.length) result.push({ key: id, id, title: labels.certificates || 'Certificates' })
        break
      case 'custom':
        customSections.value.forEach((section, index) =>
          result.push({ key: `custom-${index}`, id, title: section.sectionTitle, customIndex: index }))
        break
      case 'links':
        if (websites.value.length) result.push({ key: id, id, title: labels.websites })
        break
      case 'hobbies':
        if (hobbies.value.length) result.push({ key: id, id, title: labels.hobbies || 'Hobbies' })
        break
    }
  }
  return result
})

const indexLabel = (index: number) => String(index + 1).padStart(2, '0')

const getExperienceRole = (exp: TimelineExperience | CvExperience | OldExperience): string =>
  exp?.title ?? exp?.position ?? ''

const isCurrentRole = (exp: TimelineExperience | CvExperience | OldExperience): boolean =>
  Boolean((exp as TimelineExperience).startDate && !(exp as TimelineExperience).endDate)

const hasContact = computed(() =>
  Boolean(contact.value?.email || contact.value?.phone || contact.value?.website || contact.value?.linkedin || contact.value?.location))
</script>

<template>
  <div
    v-if="props.cvData"
    :dir="isRTL ? 'rtl' : 'ltr'"
    class="sig"
    :style="rootStyle"
  >

    <!-- Header: portrait, name and role on an accent-tinted band -->
    <header class="sig-hero">
      <div class="sig-shell sig-hero-grid">
        <div class="sig-portrait">
          <img v-if="props.cvData.photoUrl" :src="props.cvData.photoUrl" :alt="props.cvData.name" class="sig-portrait-img">
          <div v-else class="sig-portrait-initials" aria-hidden="true">{{ initials }}</div>
        </div>

        <div class="sig-hero-text">
          <h1 class="sig-name">{{ props.cvData.name }}</h1>
          <p v-if="props.cvData.title" class="sig-role">{{ props.cvData.title }}</p>

          <ul v-if="personalDetails.length" class="sig-facts">
            <li v-for="item in personalDetails" :key="item.key" class="sig-fact">
              <i :class="item.icon" class="sig-fact-icon" aria-hidden="true" />
              <span class="sr-only">{{ item.label }}:</span>
              <span class="sig-fact-value">{{ item.value }}</span>
            </li>
          </ul>

          <div v-if="contact?.email || contact?.phone" class="sig-actions">
            <a v-if="contact?.email" :href="`mailto:${contact.email}`" class="sig-btn sig-btn-solid">
              <i class="bi bi-envelope" aria-hidden="true" />
              <span class="sig-btn-label">{{ sectionTranslations.getInTouch }}</span>
            </a>
            <a v-if="contact?.phone" :href="`tel:${contact.phone}`" class="sig-btn sig-btn-outline">
              <i class="bi bi-telephone" aria-hidden="true" />
              <span class="sig-btn-label">{{ contact.phone }}</span>
            </a>
          </div>
        </div>
      </div>
    </header>

    <!-- Body: the sections in the CV's own order -->
    <main class="sig-shell sig-body">
      <section
        v-for="(block, blockIndex) in blocks"
        :id="block.customIndex === undefined ? block.id : `custom-section-${block.customIndex}`"
        :key="block.key"
        class="sig-section"
      >
        <div class="sig-section-head">
          <div class="sig-section-index" aria-hidden="true">{{ indexLabel(blockIndex) }}</div>
          <h2 class="sig-section-title">{{ block.title }}</h2>
        </div>

        <div class="sig-section-body">
          <!-- About -->
          <p v-if="block.id === 'about'" class="sig-lead">{{ props.cvData.aboutMe }}</p>

          <!-- Experience: a rail with one node per role, the current one filled -->
          <ol v-else-if="block.id === 'experience'" class="sig-timeline">
            <li v-for="(exp, index) in props.experiencesList" :key="index" class="sig-timeline-item">
              <i class="sig-node" :class="{ 'sig-node-current': isCurrentRole(exp) }" aria-hidden="true" />
              <div class="sig-item-head">
                <h3 class="sig-item-title">{{ getExperienceRole(exp) }}</h3>
                <span v-if="formatPeriod(exp)" class="sig-pill">{{ formatPeriod(exp) }}</span>
              </div>
              <p v-if="exp.company" class="sig-item-sub">{{ exp.company }}</p>
              <p v-if="exp.description" class="sig-text sig-pre">{{ exp.description }}</p>
            </li>
          </ol>

          <!-- Education -->
          <ul v-else-if="block.id === 'education'" class="sig-stack">
            <li v-for="(edu, index) in props.educationList" :key="index" class="sig-row">
              <div class="sig-item-head">
                <h3 class="sig-item-title">{{ edu.degree || edu.school }}</h3>
                <span v-if="formatPeriod(edu)" class="sig-pill">{{ formatPeriod(edu) }}</span>
              </div>
              <p v-if="edu.degree && edu.school" class="sig-item-sub">{{ edu.school }}</p>
            </li>
          </ul>

          <!-- Skills: two groups of chips, dots when levels are on -->
          <div v-else-if="block.id === 'skills'" class="sig-skill-groups">
            <div v-if="skills.technical.length" class="sig-skill-group">
              <h3 v-if="skills.soft.length" class="sig-group-title">{{ sectionTranslations.technicalSkills }}</h3>
              <ul class="sig-chips">
                <li v-for="skill in skills.technical" :key="skill" class="sig-chip">
                  {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="sig-dots" />
                </li>
              </ul>
            </div>
            <div v-if="skills.soft.length" class="sig-skill-group">
              <h3 class="sig-group-title">{{ sectionTranslations.softSkills }}</h3>
              <ul class="sig-chips">
                <li v-for="skill in skills.soft" :key="skill" class="sig-chip">
                  {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="sig-dots" />
                </li>
              </ul>
            </div>
          </div>

          <!-- Languages: a bar only for a level we can read, with levels on -->
          <ul v-else-if="block.id === 'languages'" class="sig-languages">
            <li v-for="lang in languages" :key="lang.name" class="sig-language">
              <div class="sig-item-head">
                <h3 class="sig-item-title">{{ lang.name }}</h3>
                <span v-if="lang.level" class="sig-muted">{{ languageLevel(lang.level) }}</span>
              </div>
              <div v-if="languagePercent(lang.level) > 0" class="sig-meter" aria-hidden="true">
                <i class="sig-meter-fill" :style="{ width: `${languagePercent(lang.level)}%` }" />
              </div>
            </li>
          </ul>

          <!-- Certificates -->
          <ul v-else-if="block.id === 'certificates'" class="sig-stack">
            <li v-for="(cert, index) in certificates" :key="index" class="sig-row">
              <a v-if="cert.url" :href="cert.url" target="_blank" rel="noopener noreferrer" class="sig-item-title sig-link">
                {{ cert.name }} <i class="bi bi-arrow-up-right" aria-hidden="true" />
              </a>
              <h3 v-else class="sig-item-title">{{ cert.name }}</h3>
              <p v-if="cert.issuer || cert.date" class="sig-item-sub">
                {{ [cert.issuer, formatCertificateDate(cert.date)].filter(Boolean).join(' · ') }}
              </p>
            </li>
          </ul>

          <!-- A custom section: cards, with the item's image when it has one -->
          <ul v-else-if="block.id === 'custom' && block.customIndex !== undefined" class="sig-cards">
            <li v-for="(item, index) in customSections[block.customIndex]!.items" :key="index" class="sig-card">
              <img v-if="item.image" :src="item.image" :alt="item.title" class="sig-card-img" loading="lazy">
              <div class="sig-card-body">
                <a v-if="item.url" :href="item.url" target="_blank" rel="noopener noreferrer" class="sig-item-title sig-link">
                  {{ item.title }} <i class="bi bi-arrow-up-right" aria-hidden="true" />
                </a>
                <h3 v-else class="sig-item-title">{{ item.title }}</h3>
                <p v-if="item.text" class="sig-text sig-pre">{{ item.text }}</p>
              </div>
            </li>
          </ul>

          <!-- Links -->
          <ul v-else-if="block.id === 'links'" class="sig-links">
            <li v-for="site in websites" :key="site.url">
              <a :href="site.url" target="_blank" rel="noopener noreferrer" class="sig-link-card">
                <i :class="getLinkMeta(site).biClass" class="sig-link-icon" aria-hidden="true" />
                <span class="sig-link-name">{{ getLinkDisplayName(site) }}</span>
                <i class="bi bi-arrow-up-right sig-link-arrow" aria-hidden="true" />
              </a>
            </li>
          </ul>

          <!-- Hobbies -->
          <ul v-else-if="block.id === 'hobbies'" class="sig-chips">
            <li v-for="(hobby, index) in hobbies" :key="index" class="sig-chip sig-chip-plain">{{ hobby }}</li>
          </ul>
        </div>
      </section>
    </main>

    <!-- Contact: the one full accent band, always last -->
    <section v-if="hasContact || (props.showQRCode && publicCardUrl)" id="contact" class="sig-contact">
      <div class="sig-shell sig-contact-grid">
        <div>
          <h2 class="sig-contact-title">{{ sectionTranslations.letsConnect }}</h2>
          <p class="sig-contact-text">{{ sectionTranslations.openToOpportunities }}</p>
          <ul class="sig-contact-list">
            <li v-if="contact?.email">
              <a :href="`mailto:${contact.email}`" class="sig-contact-item">
                <i class="bi bi-envelope" aria-hidden="true" />
                <span class="sig-contact-value">{{ contact.email }}</span>
              </a>
            </li>
            <li v-if="contact?.phone">
              <a :href="`tel:${contact.phone}`" class="sig-contact-item">
                <i class="bi bi-telephone" aria-hidden="true" />
                <span class="sig-contact-value">{{ contact.phone }}</span>
              </a>
            </li>
            <li v-if="contact?.website">
              <a :href="contact.website" target="_blank" rel="noopener noreferrer" class="sig-contact-item">
                <i class="bi bi-globe2" aria-hidden="true" />
                <span class="sig-contact-value">{{ contact.website }}</span>
              </a>
            </li>
            <li v-if="contact?.linkedin">
              <a :href="contact.linkedin" target="_blank" rel="noopener noreferrer" class="sig-contact-item">
                <i class="bi bi-linkedin" aria-hidden="true" />
                <span class="sig-contact-value">{{ contact.linkedin }}</span>
              </a>
            </li>
            <li v-if="contact?.location">
              <div class="sig-contact-item">
                <i class="bi bi-geo-alt" aria-hidden="true" />
                <span class="sig-contact-value">{{ contact.location }}</span>
              </div>
            </li>
          </ul>
        </div>

        <div v-if="props.showQRCode && publicCardUrl" class="sig-qr">
          <CommonQRCode :link="publicCardUrl" :size="150" background-color="#FFFFFF" foreground-color="#0E0E0E" />
          <p class="sig-qr-text">{{ sectionTranslations.scanToSaveContact }}</p>
        </div>
      </div>
    </section>

    <footer class="sig-footer">
      <span class="sig-muted">{{ sectionTranslations.createdWith }}</span>
      <a href="https://www.inmyresume.com" target="_blank" rel="noopener noreferrer" class="sig-footer-logo">
        <img src="/logo.svg" alt="InMyResume" width="264" height="56" class="sig-logo sig-logo-light">
        <img src="/logo_white.svg" alt="InMyResume" width="264" height="56" class="sig-logo sig-logo-dark">
      </a>
    </footer>
  </div>

  <!-- No data state -->
  <div v-else class="sig sig-empty">
    <h3 class="sig-item-title">{{ sectionTranslations.noDataAvailable }}</h3>
    <p class="sig-text">{{ sectionTranslations.noDataDescription }}</p>
  </div>
</template>

<!-- The picked typeface: the same cuts the PDF embeds (DM Sans and Inter are global). -->
<style src="~/assets/fonts/cv-design-fonts.css"></style>

<style scoped>
/*
 * Every rule is `.sig .x`, one class more than it needs: the site styles bare
 * elements from `:root` and `.sofax` (assets/styles/modules), with dark-mode
 * variants that reach (0,2,1). Two classes and the scope attribute outrank
 * them, so the template looks the same on the card page, in the editor and in
 * the iframe preview.
 */
.sig {
  --sig-bg: #ffffff;
  --sig-surface: #f6f7f8;
  --sig-ink: #0e0e0e;
  --sig-body: #3f4246;
  --sig-muted: #6b6f75;
  --sig-line: #e6e8eb;
  --sig-accent-text: var(--sig-accent-text-light);
  --sig-accent-soft: color-mix(in srgb, var(--sig-accent) 10%, var(--sig-bg));
  --sig-accent-tint: color-mix(in srgb, var(--sig-accent) 5%, var(--sig-bg));
  --sig-step: calc(var(--sig-space) * 1rem);

  position: relative;
  container-type: inline-size;
  background: var(--sig-bg);
  color: var(--sig-body);
  font-family: var(--sig-body-font);
  font-size: var(--sig-size);
  line-height: 1.6;
  text-align: start;
}

:where(.dark-mode) .sig {
  --sig-bg: #0f1115;
  --sig-surface: #171a20;
  --sig-ink: #ffffff;
  --sig-body: #c9cdd3;
  --sig-muted: #959aa2;
  --sig-line: #262a31;
  --sig-accent-text: var(--sig-accent-text-dark);
  --sig-accent-soft: color-mix(in srgb, var(--sig-accent) 22%, var(--sig-bg));
  --sig-accent-tint: color-mix(in srgb, var(--sig-accent) 9%, var(--sig-bg));
}

/* Reset for whatever the site paints on bare elements. */
.sig :where(h1, h2, h3, p, span, ul, ol, li, a) {
  margin: 0;
  padding: 0;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  letter-spacing: normal;
  color: inherit;
  list-style: none;
  text-decoration: none;
}

.sig .sig-shell {
  width: 100%;
  max-width: 68rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}

.sig .sig-muted {
  color: var(--sig-muted);
  font-size: 0.875em;
}

/* ---------- Header ---------- */

.sig .sig-hero {
  position: relative;
  overflow: hidden;
  background: var(--sig-accent-tint);
  border-top: 6px solid var(--sig-accent);
  padding-block: calc(var(--sig-step) * 3) calc(var(--sig-step) * 2.75);
}

/* One large accent ring in the corner: the header's only decoration. */
.sig .sig-hero::after {
  content: '';
  position: absolute;
  inset-inline-end: -9rem;
  top: -9rem;
  width: 22rem;
  height: 22rem;
  border-radius: 50%;
  border: 2.5rem solid var(--sig-accent);
  opacity: 0.08;
  pointer-events: none;
}

.sig .sig-hero-grid {
  position: relative;
  z-index: 1;
  display: grid;
  gap: calc(var(--sig-step) * 1.75);
  justify-items: start;
}

.sig .sig-portrait {
  width: 8.5rem;
  aspect-ratio: 1;
  border-radius: 1.5rem;
  overflow: hidden;
  background: var(--sig-accent);
  box-shadow: 0 0 0 4px var(--sig-bg), 0 0 0 6px var(--sig-accent);
  flex-shrink: 0;
}

.sig .sig-portrait-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sig .sig-portrait-initials {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: var(--sig-on-accent);
  font-family: var(--sig-heading-font);
  font-size: 2.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.sig .sig-name {
  color: var(--sig-ink);
  font-family: var(--sig-heading-font);
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
}

.sig .sig-role {
  margin-top: 0.6rem;
  color: var(--sig-accent-text);
  font-family: var(--sig-heading-font);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
}

.sig .sig-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  margin-top: calc(var(--sig-step) * 1.1);
}

.sig .sig-fact {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375em;
}

.sig .sig-fact-icon {
  color: var(--sig-accent-text);
}

.sig .sig-fact-value {
  color: var(--sig-body);
}

.sig .sig-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: calc(var(--sig-step) * 1.5);
}

.sig .sig-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  border-radius: 999px;
  padding: 0.7rem 1.35rem;
  font-family: var(--sig-heading-font);
  font-size: 0.9375em;
  font-weight: 700;
  line-height: 1.2;
  transition: transform 200ms ease, background-color 200ms ease, color 200ms ease;
}

.sig .sig-btn:hover {
  transform: translateY(-2px);
}

.sig .sig-btn-solid {
  background: var(--sig-accent);
  color: var(--sig-on-accent);
}

.sig .sig-btn-outline {
  border: 1.5px solid var(--sig-accent);
  color: var(--sig-accent-text);
  background: var(--sig-bg);
}

.sig .sig-btn-label {
  color: inherit;
  font-size: inherit;
}

/* ---------- Body ---------- */

.sig .sig-body {
  padding-block: calc(var(--sig-step) * 1.5) calc(var(--sig-step) * 3);
}

.sig .sig-section {
  display: grid;
  gap: calc(var(--sig-step) * 1.1);
  padding-block: calc(var(--sig-step) * 2);
  border-bottom: 1px solid var(--sig-line);
}

.sig .sig-section:last-child {
  border-bottom: 0;
}

.sig .sig-section-head {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.sig .sig-section-index {
  color: var(--sig-accent-text);
  font-family: var(--sig-heading-font);
  font-size: 0.875em;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.sig .sig-section-title {
  color: var(--sig-ink);
  font-family: var(--sig-heading-font);
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.sig .sig-section-body {
  min-width: 0;
}

.sig .sig-lead {
  padding-inline-start: 1.25rem;
  border-inline-start: 3px solid var(--sig-accent);
  color: var(--sig-ink);
  font-size: 1.1875em;
  line-height: 1.65;
  white-space: pre-line;
}

.sig .sig-item-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 1rem;
}

.sig .sig-item-title {
  color: var(--sig-ink);
  font-family: var(--sig-heading-font);
  font-size: 1.125em;
  font-weight: 700;
  line-height: 1.35;
}

.sig .sig-item-sub {
  margin-top: 0.2rem;
  color: var(--sig-accent-text);
  font-weight: 600;
  font-size: 0.9375em;
}

.sig .sig-text {
  margin-top: 0.6rem;
  color: var(--sig-body);
  font-size: 0.9375em;
  line-height: 1.65;
}

.sig .sig-pre {
  white-space: pre-line;
}

.sig .sig-pill {
  flex-shrink: 0;
  border-radius: 999px;
  padding: 0.2rem 0.75rem;
  background: var(--sig-accent-soft);
  color: var(--sig-accent-text);
  font-size: 0.8125em;
  font-weight: 600;
  white-space: nowrap;
}

.sig .sig-link {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  transition: color 200ms ease;
}

.sig .sig-link:hover {
  color: var(--sig-accent-text);
}

/* Experience timeline */
.sig .sig-timeline {
  position: relative;
  display: grid;
  gap: calc(var(--sig-step) * 1.6);
}

.sig .sig-timeline::before {
  content: '';
  position: absolute;
  inset-block: 0.5rem 0.5rem;
  inset-inline-start: 0.4rem;
  width: 2px;
  background: linear-gradient(to bottom, var(--sig-accent), var(--sig-line));
}

.sig .sig-timeline-item {
  position: relative;
  padding-inline-start: 2rem;
}

.sig .sig-node {
  position: absolute;
  inset-inline-start: 0;
  top: 0.4rem;
  width: 0.95rem;
  height: 0.95rem;
  border-radius: 50%;
  border: 2px solid var(--sig-accent);
  background: var(--sig-bg);
}

.sig .sig-node-current {
  background: var(--sig-accent);
  box-shadow: 0 0 0 4px var(--sig-accent-soft);
}

/* Education and certificates */
.sig .sig-stack {
  display: grid;
  gap: 1rem;
}

.sig .sig-row {
  padding-bottom: 1rem;
  border-bottom: 1px dashed var(--sig-line);
}

.sig .sig-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

/* Skills */
.sig .sig-skill-groups {
  display: grid;
  gap: calc(var(--sig-step) * 1.4);
}

.sig .sig-group-title {
  margin-bottom: 0.75rem;
  color: var(--sig-muted);
  font-size: 0.75em;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sig .sig-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.sig .sig-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  background: var(--sig-accent-soft);
  color: var(--sig-ink);
  font-size: 0.875em;
  font-weight: 600;
  line-height: 1.4;
}

.sig .sig-chip-plain {
  background: transparent;
  border: 1px solid var(--sig-line);
  font-weight: 500;
}

.sig .sig-dots {
  color: var(--sig-accent-text);
}

/* Languages */
.sig .sig-languages {
  display: grid;
  gap: 1rem 2rem;
}

.sig .sig-meter {
  margin-top: 0.5rem;
  height: 0.375rem;
  border-radius: 999px;
  background: var(--sig-surface);
  overflow: hidden;
}

.sig .sig-meter-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--sig-accent);
}

/* Custom sections */
.sig .sig-cards {
  display: grid;
  gap: 1rem;
}

.sig .sig-card {
  overflow: hidden;
  border: 1px solid var(--sig-line);
  border-radius: 1rem;
  background: var(--sig-bg);
  transition: border-color 200ms ease, transform 200ms ease;
}

.sig .sig-card:hover {
  border-color: var(--sig-accent);
  transform: translateY(-2px);
}

.sig .sig-card-img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.sig .sig-card-body {
  padding: 1.1rem 1.25rem 1.25rem;
}

/* Links */
.sig .sig-links {
  display: grid;
  gap: 0.75rem;
}

.sig .sig-link-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  border: 1px solid var(--sig-line);
  border-radius: 0.875rem;
  padding: 0.85rem 1rem;
  color: var(--sig-ink);
  transition: border-color 200ms ease, background-color 200ms ease;
}

.sig .sig-link-card:hover {
  border-color: var(--sig-accent);
  background: var(--sig-accent-tint);
}

.sig .sig-link-icon {
  display: flex;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.625rem;
  background: var(--sig-accent-soft);
  color: var(--sig-accent-text);
}

.sig .sig-link-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: var(--sig-ink);
  font-weight: 600;
  font-size: 0.9375em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sig .sig-link-arrow {
  color: var(--sig-muted);
}

/* ---------- Contact band ---------- */

.sig .sig-contact {
  background: var(--sig-accent);
  color: var(--sig-on-accent);
  padding-block: calc(var(--sig-step) * 3);
}

.sig .sig-contact-grid {
  display: grid;
  gap: calc(var(--sig-step) * 2);
  align-items: center;
}

.sig .sig-contact-title {
  color: var(--sig-on-accent);
  font-family: var(--sig-heading-font);
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.sig .sig-contact-text {
  margin-top: 0.6rem;
  color: var(--sig-on-accent);
  opacity: 0.85;
}

.sig .sig-contact-list {
  display: grid;
  gap: 0.6rem;
  margin-top: calc(var(--sig-step) * 1.5);
}

.sig .sig-contact-item {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 0.75rem;
  color: var(--sig-on-accent);
  font-weight: 600;
}

.sig a.sig-contact-item:hover .sig-contact-value {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.sig .sig-contact-value {
  min-width: 0;
  overflow-wrap: anywhere;
  color: inherit;
  font-size: inherit;
}

.sig .sig-qr {
  justify-self: start;
  border-radius: 1rem;
  background: #ffffff;
  padding: 1rem;
  text-align: center;
}

.sig .sig-qr-text {
  max-width: 150px;
  margin-top: 0.5rem;
  color: #0e0e0e;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
}

/* ---------- Footer ---------- */

.sig .sig-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 2rem 1.25rem;
  border-top: 1px solid var(--sig-line);
}

.sig .sig-logo {
  height: 1.5rem;
  width: auto;
}

.sig .sig-logo-dark {
  display: none;
}

:where(.dark-mode) .sig .sig-logo-light {
  display: none;
}

:where(.dark-mode) .sig .sig-logo-dark {
  display: block;
}

.sig.sig-empty {
  display: grid;
  min-height: 60vh;
  place-content: center;
  gap: 0.5rem;
  padding: 2rem;
  text-align: center;
}

/* ---------- Wider: by the template's own width ---------- */

@container (min-width: 560px) {
  .sig .sig-languages,
  .sig .sig-cards,
  .sig .sig-links {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (min-width: 640px) {
  .sig .sig-shell {
    padding-inline: 2.5rem;
  }

  .sig .sig-hero-grid {
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: calc(var(--sig-step) * 2.5);
  }

  .sig .sig-portrait {
    width: 11rem;
  }

  .sig .sig-name {
    font-size: 3.25rem;
  }

  .sig .sig-contact-grid {
    grid-template-columns: minmax(0, 1fr) auto;
  }
}

/* Section heading in its own column, the content beside it. */
@container (min-width: 760px) {
  .sig .sig-section {
    grid-template-columns: 13rem minmax(0, 1fr);
    gap: 2.5rem;
  }

  .sig .sig-section-head {
    flex-direction: column;
    gap: 0.35rem;
  }

  .sig .sig-section-title {
    font-size: 1.375rem;
  }

  .sig .sig-skill-groups {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sig .sig-skill-group:only-child {
    grid-column: 1 / -1;
  }
}

@container (min-width: 1000px) {
  .sig .sig-name {
    font-size: 4rem;
  }

  .sig .sig-portrait {
    width: 13rem;
  }

  .sig .sig-cards,
  .sig .sig-links {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .sig .sig-contact-title {
    font-size: 2.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sig .sig-btn,
  .sig .sig-card,
  .sig .sig-link,
  .sig .sig-link-card {
    transition: none;
  }

  .sig .sig-btn:hover,
  .sig .sig-card:hover {
    transform: none;
  }
}
</style>
